import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { getThemeColors } from '../utils/themeColors';
import reviewsData from '../data/reviews.json';
import CTACard_1 from './CTACard_1';
import CTACard_2 from './CTACard_2';
import CTACard_3 from './CTACard_3';
import CTACard_4 from './CTACard_4';

interface Review {
  id: number;
  name: string;
  rating: number;
  date: string;
  text: string;
  keywords: string[];
}

interface ReviewsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ReviewsModal: React.FC<ReviewsModalProps> = ({ isOpen, onClose }) => {
  const { isDark, isHighContrast } = useTheme();
  const colors = getThemeColors('dark', isDark, isHighContrast);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [animatedReviews, setAnimatedReviews] = useState<Set<number>>(new Set());
  const [showCTA, setShowCTA] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [ctaShown, setCtaShown] = useState(false);
  const [ctaTimeout, setCtaTimeout] = useState<NodeJS.Timeout | null>(null);
  const [scrollBasedCTA, setScrollBasedCTA] = useState(false);
  const [currentCTAType, setCurrentCTAType] = useState(1); // 1, 2, 3, 4
  const [ctaShownCount, setCtaShownCount] = useState(0); // Contador de CTAs mostrados
  const [cta1Shown, setCta1Shown] = useState(false);
  const [cta2Shown, setCta2Shown] = useState(false);
  const [cta3Shown, setCta3Shown] = useState(false);
  const [cta4Shown, setCta4Shown] = useState(false);
  const [pagesScrolled, setPagesScrolled] = useState(0);

  // Usar useMemo para evitar re-renders desnecessários
  const allReviews: Review[] = useMemo(() => reviewsData || [], []);
  const allReviewsRef = useRef(allReviews);
  
  // Atualizar ref quando allReviews mudar
  useEffect(() => {
    allReviewsRef.current = allReviews;
  }, [allReviews]);

  const loadMoreReviews = useCallback(async () => {
    if (loading || !hasMore || !allReviewsRef.current.length) return;
    
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 300));
      const itemsPerPage = 20;
      const startIndex = (page - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const newReviews = allReviewsRef.current.slice(startIndex, endIndex);
      
      setReviews(prev => [...prev, ...newReviews]);
      setPage(prev => prev + 1);
      setHasMore(endIndex < allReviewsRef.current.length);
      
      // Animar os novos reviews um por um
      newReviews.forEach((review, index) => {
        setTimeout(() => {
          setAnimatedReviews(prev => new Set([...Array.from(prev), review.id]));
        }, index * 100); // 100ms de delay entre cada card
      });
      
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  }, [page, loading, hasMore]);

  // Função para alternar o tipo de CTA baseado no comportamento
  const getNextCTAType = useCallback(() => {
    // Lógica inteligente: alternar entre CTAs baseado no contador
    const ctaTypes = [1, 2, 3, 4];
    return ctaTypes[ctaShownCount % ctaTypes.length];
  }, [ctaShownCount]);

  // Função para mostrar CTA com tipo específico
  const showCTAWithType = useCallback((ctaType: number) => {
    setCurrentCTAType(ctaType);
    setShowCTA(true);
    setCtaShown(true);
    setCtaShownCount(prev => prev + 1);
  }, []);

  // Função para mostrar CTA baseado no contexto
  const showContextualCTA = useCallback(() => {
    let nextType = 1;
    
    // Distribuir 4 CTAs igualmente ao longo do scroll
    // Se chegou até página 8, distribuir: 1, 3, 5, 7
    if (pagesScrolled >= 1 && !cta1Shown) {
      nextType = 1;
      setCta1Shown(true);
    } else if (pagesScrolled >= 3 && !cta2Shown) {
      nextType = 2;
      setCta2Shown(true);
    } else if (pagesScrolled >= 5 && !cta3Shown) {
      nextType = 3;
      setCta3Shown(true);
    } else if (pagesScrolled >= 7 && !cta4Shown) {
      nextType = 4;
      setCta4Shown(true);
    } else {
      return; // Não mostrar CTA se não atender aos critérios
    }
    
    console.log(`=== MOSTRANDO CTA TIPO ${nextType} (${pagesScrolled} páginas roladas) ===`);
    
    // Fechar CTA atual antes de mostrar o novo
    setShowCTA(false);
    
    // Pequeno delay para garantir que o CTA anterior foi fechado
    setTimeout(() => {
      setCurrentCTAType(nextType);
      setShowCTA(true);
      setCtaShown(true);
    }, 100);
  }, [pagesScrolled, cta1Shown, cta2Shown, cta3Shown, cta4Shown]);

  // Carregar reviews iniciais quando o modal abre
  useEffect(() => {
    if (isOpen && reviews.length === 0) {
      console.log('Inicializando modal de reviews');
      setReviews([]);
      setPage(1);
      setHasMore(true);
      setAnimatedReviews(new Set());
      setShowCTA(false);
      setInitialLoadComplete(false);
      setCtaShown(false);
      setScrollBasedCTA(false);
      setCurrentCTAType(1);
      setCtaShownCount(0);
      setCta1Shown(false);
      setCta2Shown(false);
      setCta3Shown(false);
      setCta4Shown(false);
      setPagesScrolled(0);
      
      // Limpar timeout anterior se existir
      if (ctaTimeout) {
        clearTimeout(ctaTimeout);
      }
      
      // Carregar primeira página
      const itemsPerPage = 20;
      const initialReviews = allReviewsRef.current.slice(0, itemsPerPage);
      setReviews(initialReviews);
      setPage(2);
      setHasMore(itemsPerPage < allReviewsRef.current.length);
      
      // Animar os reviews iniciais um por um
      initialReviews.forEach((review, index) => {
        setTimeout(() => {
          setAnimatedReviews(prev => new Set([...Array.from(prev), review.id]));
        }, index * 80);
      });

      // Mostrar CTA inicial após 1 página ser rolada
      const timeout = setTimeout(() => {
        setInitialLoadComplete(true);
        setPagesScrolled(1); // Simular que 1 página foi rolada
        showContextualCTA();
      }, 3000); // Reduzir para 3 segundos
      
      setCtaTimeout(timeout);
    }
  }, [isOpen, ctaTimeout, showContextualCTA]);

  // Cleanup timeout quando componente desmontar
  useEffect(() => {
    return () => {
      if (ctaTimeout) {
        clearTimeout(ctaTimeout);
      }
    };
  }, [ctaTimeout]);

  // Debug: monitorar mudanças no showCTA
  useEffect(() => {
    // console.log('showCTA mudou para:', showCTA);
  }, [showCTA]);

  // Debug: monitorar mudanças no initialLoadComplete
  useEffect(() => {
    // console.log('initialLoadComplete mudou para:', initialLoadComplete);
  }, [initialLoadComplete]);

  // Debug: monitorar quando CTA deve ser renderizado
  useEffect(() => {
    // console.log('=== DEBUG CTA ===', { 
    //   showCTA, 
    //   initialLoadComplete, 
    //   shouldRender: showCTA && initialLoadComplete,
    //   isOpen,
    //   reviewsLength: reviews.length
    // });
  }, [showCTA, initialLoadComplete, isOpen, reviews.length]);

  // Debug: monitorar mudanças no currentCTAType
  useEffect(() => {
    console.log('=== DEBUG CTA ===', { 
      currentCTAType, 
      showCTA, 
      ctaShown,
      pagesScrolled,
      cta1Shown,
      cta2Shown,
      cta3Shown,
      cta4Shown
    });
  }, [currentCTAType, showCTA, ctaShown, pagesScrolled, cta1Shown, cta2Shown, cta3Shown, cta4Shown]);

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} style={{ 
        color: i < rating ? '#FFD700' : '#E5E7EB',
        fontSize: '1rem',
        marginRight: '2px'
      }}>
        ★
      </span>
    ));
  };

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const threshold = scrollHeight - scrollTop <= clientHeight * 1.5;
    
    if (threshold && !loading && hasMore) {
      loadMoreReviews();
    }
    
    // Calcular páginas roladas baseado no scroll de forma mais precisa
    const scrollPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100;
    const estimatedPages = Math.max(1, Math.floor(scrollPercentage / 12)); // 12% por página para permitir mais páginas
    
    if (estimatedPages !== pagesScrolled) {
      console.log(`Páginas roladas: ${estimatedPages} (${scrollPercentage.toFixed(1)}% scroll)`);
      setPagesScrolled(estimatedPages);
      
      // Verificar se deve mostrar CTA baseado no número de páginas
      if (estimatedPages >= 1 && !cta1Shown) {
        showContextualCTA();
      } else if (estimatedPages >= 3 && !cta2Shown) {
        showContextualCTA();
      } else if (estimatedPages >= 5 && !cta3Shown) {
        showContextualCTA();
      } else if (estimatedPages >= 7 && !cta4Shown) {
        showContextualCTA();
      }
    }
  }, [loadMoreReviews, loading, hasMore, page, pagesScrolled, cta1Shown, cta2Shown, cta3Shown, cta4Shown, showContextualCTA]);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'clamp(20px, 5vw, 40px)'
    }}>
      <div style={{
        background: colors.cardBg,
        borderRadius: 20,
        maxWidth: 800,
        width: '100%',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        border: `1px solid ${colors.borderColor}`,
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          padding: 'clamp(1.5rem, 4vw, 2rem)',
          borderBottom: `1px solid ${colors.borderColor}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: colors.cardBg
        }}>
          <div>
            <h2 style={{
              fontSize: 'clamp(1.5rem, 4vw, 2rem)',
              fontWeight: 700,
              color: colors.primaryText,
              margin: 0
            }}>
              Todas as Avaliações
            </h2>
            <p style={{
              fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
              color: colors.secondaryText,
              margin: '0.5rem 0 0 0'
            }}>
              {allReviews.length} avaliações do Google Reviews
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              color: colors.secondaryText,
              cursor: 'pointer',
              padding: '0.5rem',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div 
          style={{
            height: 'calc(90vh - clamp(5rem, 8vw, 6rem))',
            overflowY: 'auto',
            padding: 'clamp(1rem, 3vw, 1.5rem)'
          }}
          onScroll={handleScroll}
        >
          {reviews.length === 0 && !loading ? (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              color: colors.secondaryText
            }}>
              Nenhuma avaliação encontrada
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gap: 'clamp(1rem, 3vw, 1.5rem)',
              gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(300px, 45vw, 380px), 1fr))'
            }}>
              {reviews.map((review, index) => (
                <div
                  key={`${review.id}-${index}`}
                  style={{
                    background: colors.cardBg,
                    borderRadius: 12,
                    padding: 'clamp(1rem, 3vw, 1.5rem)',
                    border: `1px solid ${colors.borderColor}`,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: animatedReviews.has(review.id) ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
                    opacity: animatedReviews.has(review.id) ? 1 : 0,
                    boxShadow: animatedReviews.has(review.id) 
                      ? `0 8px 25px ${colors.shadowColor}` 
                      : '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  {/* Header do Review */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    marginBottom: 'clamp(0.8rem, 2vw, 1rem)'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.3rem',
                        flexWrap: 'wrap'
                      }}>
                        <span style={{
                          fontWeight: 600,
                          color: colors.primaryText,
                          fontSize: 'clamp(0.9rem, 2.5vw, 1rem)'
                        }}>
                          {review.name}
                        </span>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.8rem',
                        color: colors.secondaryText,
                        flexWrap: 'wrap'
                      }}>
                        <span>• {review.date}</span>
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.3rem'
                    }}>
                      {renderStars(review.rating)}
                    </div>
                  </div>

                  {/* Texto do Review */}
                  <div style={{
                    color: colors.primaryText,
                    fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                    lineHeight: 1.5,
                    marginBottom: 'clamp(0.8rem, 2vw, 1rem)'
                  }}>
                    {review.text}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CTA Card - Sobreposição Fixa */}
          {(showCTA || scrollBasedCTA) && initialLoadComplete && ctaShown && (
            <>
              {/* Overlay escuro */}
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.7)',
                zIndex: 9998,
                animation: 'fadeIn 0.3s ease-out'
              }} />
              
              {/* CTA */}
              <div style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(0, 0, 0, 0.9)',
                borderRadius: 20,
                padding: 'clamp(1.5rem, 4vw, 2rem)',
                border: `2px solid ${colors.accentText}`,
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8)',
                zIndex: 9999,
                maxWidth: '90vw',
                width: 'clamp(300px, 80vw, 500px)',
                backdropFilter: 'blur(10px)',
                animation: 'fadeInScale 0.5s ease-out'
              }}>
                {currentCTAType === 1 && <CTACard_1 onClose={() => {
                  setShowCTA(false);
                  setScrollBasedCTA(false);
                  setCtaShown(false);
                }} />}
                {currentCTAType === 2 && <CTACard_2 onClose={() => {
                  setShowCTA(false);
                  setScrollBasedCTA(false);
                  setCtaShown(false);
                }} />}
                {currentCTAType === 3 && <CTACard_3 onClose={() => {
                  setShowCTA(false);
                  setScrollBasedCTA(false);
                  setCtaShown(false);
                }} />}
                {currentCTAType === 4 && <CTACard_4 onClose={() => {
                  setShowCTA(false);
                  setScrollBasedCTA(false);
                  setCtaShown(false);
                }} />}
              </div>
            </>
          )}

          {/* Loading indicator */}
          {loading && (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              color: colors.secondaryText
            }}>
              <div style={{
                display: 'inline-block',
                width: '2rem',
                height: '2rem',
                border: `3px solid ${colors.borderColor}`,
                borderTop: `3px solid ${colors.accentText}`,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <div style={{ marginTop: '1rem' }}>Carregando mais avaliações...</div>
            </div>
          )}

          {/* End of reviews */}
          {!hasMore && reviews.length > 0 && (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              color: colors.secondaryText,
              fontSize: '0.9rem',
              animation: 'fadeIn 0.5s ease-in-out'
            }}>
              Todas as avaliações foram carregadas
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes fadeInUp {
          from { 
            opacity: 0; 
            transform: translateY(30px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }

        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default ReviewsModal; 